import { BotReplyLayout } from "./bot.reply.layout.component";
import { EonUiButton } from "@eon-ui/eon-ui-components-react";
import { useNexthinkContext } from "./nexthink.context";
import { fetchHealthCheckInfo } from "@me8eon/ai.tools.nextthink";
import React, { useState, useCallback, useMemo } from "react";
import DeviceSelectionComponent from "../device.selection.component";
import { useDoAi } from "@me8eon/react_intent";
import { useJwtToken } from "@me8eon/react_authentication";

export const NexthinkHealthCheck = () => {
  const [error, setError] = useState<string | null>(null);
  const { setLoading, setHealthCheckInfo, messageAndData, t, actionFreeze, setActionFreeze, selectedDevice, setSelectedDevice } =
    useNexthinkContext();
  const {
    data: {deviceDetails},
  } = messageAndData || {};
  const handleAiQuery = useDoAi();
  const jwtTokenFn = useJwtToken();
  const options = useMemo(() => (deviceDetails?.data ?? []).map((device: any) => ({
    value: device["device.collector.uid"],
    label: device["device.name"],
  })), [deviceDetails?.data]);

  const getHealthCheckInfo = useCallback(
    async (id: string) => {
      setLoading(true);
      try {
        const healthCheckInfo = await fetchHealthCheckInfo(id, jwtTokenFn);
        if (healthCheckInfo.data.length) {
          setHealthCheckInfo(healthCheckInfo.data[0]);
        }
      } catch (err: unknown) {
        console.error("Error fetching health check info:", err);
        setError(t("chatBot.nexthinkIntegration.smartSolution.healthCheckApiFail"));
      } finally {
        setLoading(false);
      }
    },
    [setHealthCheckInfo, setLoading, t, jwtTokenFn]
  );

  const handleRunHealthCheck = () => {
    if (!selectedDevice.id) return;
    setActionFreeze((prev) => ({ ...prev, healthCheck: true }));
    getHealthCheckInfo(selectedDevice.id);
  };

  const changeDeviceDetails = useCallback((id: string) => {
    const data = {
      id,
      name: deviceDetails?.data?.find((device: any) => device["device.collector.uid"] === id)?.["device.name"] ?? '',
    };
    setSelectedDevice(prev => ({ ...prev, ...data }));
  }, [deviceDetails?.data, setSelectedDevice]);

  return (
    <>
      {selectedDevice.id && <BotReplyLayout>
        <div className="health-check">
          {options.length === 1 && (
            <>
              <span>
                {t(
                  "chatBot.nexthinkIntegration.smartSolution.systemName"
                )}
                : {selectedDevice.name || ""}
              </span>
              <br />
            </>
          )}
          <span>{t("chatBot.nexthinkIntegration.smartSolution.acknowledgeQuery")}</span>
          {options.length > 1 && <DeviceSelectionComponent
            value={selectedDevice.id || ''}
            options={options}
            changeHandler={changeDeviceDetails}
          />}
          <p>
            <b>{t("chatBot.nexthinkIntegration.smartSolution.askToRunHealthCheck")}</b>
          </p>
          <div className="ai-action-btn-wrapper">
            <EonUiButton
              fullWidthOnMobile
              text={t("chatBot.nexthinkIntegration.smartSolution.primaryBtn")}
              rank="primary"
              size="small"
              disabled={actionFreeze.healthCheck || !selectedDevice.id}
              onClick={handleRunHealthCheck}
            />
            <EonUiButton
              fullWidthOnMobile
              text={t("chatBot.nexthinkIntegration.smartSolution.secondaryBtn")}
              rank="secondary"
              size="small"
              disabled={actionFreeze.healthCheck}
              onClick={() => handleAiQuery(t("chatBot.nexthinkIntegration.declineScriptMsg"))}
            />
          </div>
          {error && <span className="error-message">{error}</span>}
        </div>
      </BotReplyLayout>}
    </>
  );
};
